import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import {
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Box,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import PropTypes from "prop-types";

/**
 * A number input component integrated with react-hook-form.
 * Handles numeric values with optional unit display (e.g., € or kWh).
 */
const FormNumberField = ({ name, unit, infoText, ...otherProps }) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const { value, onChange, ...fieldProps } = field;

  const [infoAnchorEl, setInfoAnchorEl] = useState(null);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "" || inputValue === null) {
      onChange(null);
    } else {
      const numValue = parseFloat(inputValue);
      onChange(isNaN(numValue) ? null : numValue);
    }
  };

  return (
    <>
      <TextField
        {...fieldProps}
        type="number"
        value={value ?? ""}
        onChange={handleChange}
        {...otherProps}
        error={!!error}
        helperText={error?.message || otherProps.helperText}
        slotProps={{
          htmlInput: {
            style: { textAlign: "right", paddingLeft: "10px" },
          },
          input:
            unit || infoText
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      {unit}
                      {infoText && (
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={(e) => setInfoAnchorEl(e.currentTarget)}
                          sx={{ ml: unit ? 0.5 : 0 }}
                        >
                          <HelpOutline fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }
              : undefined,
        }}
      />
      {infoText && (
        <Popover
          open={Boolean(infoAnchorEl)}
          anchorEl={infoAnchorEl}
          onClose={() => setInfoAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="body2">{infoText}</Typography>
          </Box>
        </Popover>
      )}
    </>
  );
};

FormNumberField.propTypes = {
  name: PropTypes.string.isRequired,
  unit: PropTypes.string,
  infoText: PropTypes.string,
};

export default FormNumberField;
